export const SITE_URL = "https://oc-international.com";
export const ENTITY_DESCRIPTION = "OC Recruitment (formerly OC International) is a UK-based retained executive search firm specializing exclusively in sales and commercial leadership roles in the US packaging industry. Founded by Chris Kenny, The Packaging Sales Recruiter, with 16+ years of packaging recruitment experience.";

export const personSchema = {
  "@context": "https://schema.org",
  "@type": "Person",
  "@id": `${SITE_URL}/about#chris-kenny`,
  name: "Chris Kenny",
  jobTitle: "Founder and Managing Director",
  url: `${SITE_URL}/about`,
  description: "Chris Kenny, The Packaging Sales Recruiter, has 16+ years of packaging recruitment experience and leads OC Recruitment's retained search for US packaging sales and commercial leadership roles.",
  sameAs: ["https://www.linkedin.com/in/packagingsalesrecruiter/"],
  worksFor: { "@id": `${SITE_URL}/#organization` },
  knowsAbout: ["Packaging sales recruitment", "Retained search", "Executive search", "US packaging industry", "Commercial leadership"],
};

export const organizationSchema = {
  "@context": "https://schema.org",
  "@type": ["Organization", "EmploymentAgency", "ProfessionalService"],
  "@id": `${SITE_URL}/#organization`,
  name: "OC Recruitment",
  legalName: "OC Recruitment Ltd",
  alternateName: ["OC International", "OC Recruitment Ltd", "The Packaging Sales Recruiter"],
  url: SITE_URL,
  logo: `${SITE_URL}/manus-storage/oc-recruitment-logo-optimized_db13998d.webp`,
  description: ENTITY_DESCRIPTION,
  // No founding year or street address until the owner confirms those facts.
  founder: { "@id": `${SITE_URL}/about#chris-kenny`, "@type": "Person", name: "Chris Kenny", jobTitle: "Founder and Managing Director", sameAs: personSchema.sameAs },
  telephone: "+1-312-800-0868",
  email: "chris.kenny@oc-international.com",
  address: { "@type": "PostalAddress", addressCountry: "GB" },
  contactPoint: [
    { "@type": "ContactPoint", telephone: "+1-312-800-0868", contactType: "recruitment inquiries", areaServed: "US", availableLanguage: "English" },
    { "@type": "ContactPoint", telephone: "+44-7366-391326", contactType: "recruitment inquiries", areaServed: ["GB", "US"], availableLanguage: "English" },
  ],
  sameAs: ["https://www.linkedin.com/company/ocrecruitment"],
  areaServed: { "@type": "Country", name: "United States" },
  knowsAbout: ["Packaging sales recruitment", "Retained search", "Executive search", "VP Sales", "Chief Commercial Officer", "Flexible Packaging", "Rigid Plastics", "Corrugated Packaging", "Folding Carton", "Labels and Sleeves", "Glass Packaging", "Metal Packaging", "Caps and Closures"],
};

export const websiteSchema = {
  "@context": "https://schema.org", "@type": "WebSite", "@id": `${SITE_URL}/#website`,
  name: "OC Recruitment", url: SITE_URL, inLanguage: "en-US",
  description: "Retained search for sales and commercial leadership roles across the US packaging industry.",
  publisher: { "@id": `${SITE_URL}/#organization` },
  // There is no site-search UI: intentionally do not add a SearchAction.
};

export const serviceSchema = {
  "@context": "https://schema.org", "@type": "Service", "@id": `${SITE_URL}/for-companies#service`,
  name: "Retained search for packaging sales",
  provider: { "@id": `${SITE_URL}/#organization`, "@type": "Organization", name: "OC Recruitment", url: SITE_URL },
  serviceType: "Retained executive search for packaging sales and commercial leadership",
  description: "Retained search for US packaging sales and commercial leaders across flexible packaging, rigid plastics, corrugated, folding carton, labels, glass, metal, and caps and closures.",
  areaServed: { "@type": "Country", name: "United States" },
  audience: { "@type": "Audience", audienceType: "Packaging manufacturers, converters, and distributors" },
};

export const pageSEO = {
  home: {
    title: "US Packaging Sales Recruiter | OC Recruitment",
    description: "Hire packaging sales and commercial leaders through retained search. OC Recruitment serves US manufacturers and converters. Discuss your hiring needs.",
    keywords: "packaging sales recruiter, packaging sales recruitment, packaging executive search, retained search packaging USA",
    canonical: "/", structuredData: [organizationSchema, websiteSchema],
  },
  forCompanies: {
    title: "Packaging Sales Recruitment | OC Recruitment",
    description: "Find packaging sales talent beyond job boards. Retained search for US manufacturers, converters, and distributors. Discuss your next commercial hire.",
    keywords: "hire packaging sales talent, packaging sales recruitment firm, retained search packaging, packaging executive search",
    canonical: "/for-companies", structuredData: [organizationSchema, serviceSchema],
  },
  forCandidates: {
    title: "US Packaging Sales Careers | OC Recruitment",
    description: "Explore US packaging sales and commercial leadership opportunities with OC Recruitment. Share your resume for a confidential discussion about your career.",
    keywords: "packaging sales jobs, packaging sales careers USA, packaging sales director jobs, packaging key account manager jobs",
    canonical: "/for-candidates",
  },
  process: {
    title: "Packaging Retained Search Process | OC Recruitment",
    description: "See how OC Recruitment maps, approaches, assesses, and secures US packaging sales leaders through retained search. Discuss your next commercial hire.",
    keywords: "packaging sales retained search process, packaging executive search process, retained recruitment process",
    canonical: "/process",
  },
  caseStudies: {
    title: "Packaging Recruitment Case Studies | OC Recruitment",
    description: "Explore packaging sales recruitment assignments and the commercial hiring challenges behind them. Talk to OC Recruitment about your retained search.",
    keywords: "packaging recruitment case studies, packaging sales placement examples, packaging executive search results",
    canonical: "/case-studies",
  },
  packagingSectors: {
    title: "Packaging Sales Recruitment Sectors | OC Recruitment",
    description: "Find specialist sales recruiters across eight US packaging sectors, from flexible packaging to folding carton and closures. Discuss your next sales hire.",
    keywords: "flexible packaging recruiter, rigid plastics recruitment, corrugated packaging recruiter, folding carton recruiter, glass packaging recruiter, metal packaging recruitment, labels recruiter",
    canonical: "/packaging-sectors",
  },
  about: {
    title: "Chris Kenny, Packaging Sales Recruiter | OC Recruitment",
    description: "Meet Chris Kenny, The Packaging Sales Recruiter, with 16+ years of packaging recruitment experience. UK-based retained search serving US packaging clients.",
    keywords: "Chris Kenny packaging recruiter, The Packaging Sales Recruiter, OC Recruitment, packaging recruitment specialist",
    canonical: "/about",
  },
  insights: {
    title: "Packaging Sales Hiring Insights | OC Recruitment",
    description: "Read practical advice on packaging sales hiring, retained search, and commercial leadership careers in the US. Explore OC Recruitment's latest insights.",
    keywords: "packaging recruitment blog, packaging sales hiring advice, packaging talent market, packaging sales career advice",
    canonical: "/insights",
  },
  contact: {
    title: "Contact Your Packaging Sales Recruiter | OC Recruitment",
    description: "Discuss a US packaging sales hire or your next career move with Chris Kenny at OC Recruitment. Contact us, share your resume, or call +1 (312) 800-0868.",
    keywords: "contact packaging recruiter, packaging recruitment consultation, hire packaging sales talent",
    canonical: "/contact",
  },
};
